import { useState } from "react";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../../ui/dialog";
import { toast } from "sonner";
import { Loader2, FlaskConical, AlertTriangle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const METRIC_TYPES = [
  { value: "rate", label: "Rate", desc: "Numerator / Denominator" },
  { value: "scaled_rate", label: "Scaled Rate", desc: "(Num / Denom) x Multiplier" },
  { value: "percent", label: "Percent", desc: "(Num / Denom) x 100" },
];

export default function CreateDerivedMetricModal({
  open,
  onOpenChange,
  projectId,
  dataset,
  token,
  onCreated,
}) {
  const [name, setName] = useState("");
  const [metricType, setMetricType] = useState("rate");
  const [numerator, setNumerator] = useState("");
  const [denominator, setDenominator] = useState("");
  const [multiplier, setMultiplier] = useState("");
  const [unitLabel, setUnitLabel] = useState("");
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const numericCols = dataset
    ? Object.entries(dataset.profile?.columns || {})
        .filter(([, info]) => info.is_numeric)
        .map(([col]) => col)
    : [];

  const reset = () => {
    setName("");
    setMetricType("rate");
    setNumerator("");
    setDenominator("");
    setMultiplier("");
    setUnitLabel("");
    setPreview(null);
  };

  const handleClose = (val) => {
    if (!val) reset();
    onOpenChange(val);
  };

  const canPreview = numerator && denominator && name.trim();

  const handlePreview = async () => {
    if (!canPreview) return;
    setPreviewLoading(true);
    setPreview(null);
    try {
      const res = await fetch(
        `${API}/projects/${projectId}/datasets/${dataset.dataset_id}/derived-metrics`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            name: `__preview_${Date.now()}`,
            numerator_column: numerator,
            denominator_column: denominator,
            type: metricType,
            multiplier: multiplier ? parseFloat(multiplier) : null,
            unit_label: unitLabel,
          }),
        }
      );

      if (!res.ok) {
        const err = await res.json();
        const msg = typeof err.detail === "string" ? err.detail : "Validation failed";
        toast.error(msg);
        return;
      }

      const data = await res.json();
      setPreview(data.preview);

      // Delete the preview metric immediately
      if (data.metric_id) {
        await fetch(
          `${API}/projects/${projectId}/datasets/${dataset.dataset_id}/derived-metrics/${data.metric_id}`,
          {
            method: "DELETE",
            headers: { Authorization: `Bearer ${token}` },
          }
        );
      }
    } catch {
      toast.error("Failed to generate preview");
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error("Metric name is required");
      return;
    }
    if (!numerator || !denominator) {
      toast.error("Select both numerator and denominator columns");
      return;
    }

    setSaving(true);
    try {
      const res = await fetch(
        `${API}/projects/${projectId}/datasets/${dataset.dataset_id}/derived-metrics`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            name: name.trim(),
            numerator_column: numerator,
            denominator_column: denominator,
            type: metricType,
            multiplier: multiplier ? parseFloat(multiplier) : null,
            unit_label: unitLabel,
          }),
        }
      );

      if (!res.ok) {
        const err = await res.json();
        const msg = typeof err.detail === "string" ? err.detail : "Failed to create metric";
        toast.error(msg);
        return;
      }

      const data = await res.json();
      toast.success(`Derived metric "${data.name}" created`);
      onCreated?.(data);
      handleClose(false);
    } catch {
      toast.error("Failed to create derived metric");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-xl" data-testid="create-derived-metric-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FlaskConical className="w-5 h-5 text-primary" />
            Create Derived Metric
          </DialogTitle>
          <DialogDescription>
            Define a rate-based metric from two columns in your dataset.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Metric Name */}
          <div className="space-y-1.5">
            <Label htmlFor="dm-name">Metric Name</Label>
            <Input
              id="dm-name"
              placeholder="e.g. Defects per Unit"
              value={name}
              onChange={(e) => setName(e.target.value)}
              data-testid="dm-name-input"
            />
          </div>

          {/* Metric Type */}
          <div className="space-y-1.5">
            <Label>Metric Type</Label>
            <div className="grid grid-cols-3 gap-2">
              {METRIC_TYPES.map((t) => (
                <button
                  key={t.value}
                  type="button"
                  onClick={() => setMetricType(t.value)}
                  className={`p-2.5 rounded-md border text-left transition-colors ${
                    metricType === t.value
                      ? "border-primary bg-primary/10 text-foreground"
                      : "border-border bg-background text-muted-foreground hover:border-primary/40"
                  }`}
                  data-testid={`dm-type-${t.value}`}
                >
                  <p className="text-xs font-semibold">{t.label}</p>
                  <p className="text-[10px] text-muted-foreground mt-0.5">{t.desc}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Numerator / Denominator */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Numerator Column</Label>
              <select
                value={numerator}
                onChange={(e) => setNumerator(e.target.value)}
                className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                data-testid="dm-numerator-select"
              >
                <option value="">Select column</option>
                {numericCols.map((col) => (
                  <option key={col} value={col}>{col}</option>
                ))}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label>Denominator Column</Label>
              <select
                value={denominator}
                onChange={(e) => setDenominator(e.target.value)}
                className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                data-testid="dm-denominator-select"
              >
                <option value="">Select column</option>
                {numericCols.map((col) => (
                  <option key={col} value={col}>{col}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Multiplier (only for scaled_rate) */}
          {metricType === "scaled_rate" && (
            <div className="space-y-1.5">
              <Label>Multiplier</Label>
              <Input
                type="number"
                placeholder="e.g. 1000"
                value={multiplier}
                onChange={(e) => setMultiplier(e.target.value)}
                data-testid="dm-multiplier-input"
              />
            </div>
          )}

          {/* Unit Label */}
          <div className="space-y-1.5">
            <Label>Unit Label (optional)</Label>
            <Input
              placeholder="e.g. defects/unit, %, ppm"
              value={unitLabel}
              onChange={(e) => setUnitLabel(e.target.value)}
              data-testid="dm-unit-label-input"
            />
          </div>

          {/* Preview */}
          {preview && (
            <div className="bg-muted/30 border border-border rounded-md p-3 space-y-2" data-testid="dm-preview-panel">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Preview</p>
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-border text-muted-foreground">
                    <th className="text-left py-1 px-2">Row</th>
                    <th className="text-right py-1 px-2">Numerator</th>
                    <th className="text-right py-1 px-2">Denominator</th>
                    <th className="text-right py-1 px-2 text-primary">Result</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.preview?.map((row) => (
                    <tr key={row.row} className="border-b border-border/50">
                      <td className="py-1 px-2">{row.row}</td>
                      <td className="py-1 px-2 text-right">{row.numerator ?? "-"}</td>
                      <td className="py-1 px-2 text-right">{row.denominator ?? "-"}</td>
                      <td className="py-1 px-2 text-right text-primary font-medium">
                        {row.result != null ? row.result.toFixed(4) : "-"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {preview.warnings?.length > 0 && (
                <div className="flex items-start gap-1.5 text-[10px] text-amber-400 mt-1">
                  <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
                  <span>{preview.warnings.join("; ")}</span>
                </div>
              )}
              <p className="text-[10px] text-muted-foreground">
                {preview.valid_rows}/{preview.total_rows} rows valid
              </p>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={handlePreview}
            disabled={!canPreview || previewLoading}
            data-testid="dm-preview-btn"
          >
            {previewLoading ? (
              <Loader2 className="w-4 h-4 animate-spin mr-1.5" />
            ) : (
              <FlaskConical className="w-4 h-4 mr-1.5" />
            )}
            Preview
          </Button>
          <Button
            onClick={handleSave}
            disabled={!canPreview || saving}
            data-testid="dm-save-btn"
          >
            {saving && <Loader2 className="w-4 h-4 animate-spin mr-1.5" />}
            Save Metric
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
